import { createEventSchema, type CreateEventSchema } from '@/api/events/event.schema';
import { ContentLoader } from '@/components/ui/loaders';
import { date } from '@/lib/date';
import {
  useCreateEvent,
  useDeleteEvent,
  useEvent,
  useUpdateEvent,
} from '@/services/events';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AdminFormPage, useServerErrors } from './admin-form';
import { ChoiceGroup, FormSection, TextField } from './fields';
import { ACTIVE_OPTIONS } from './sacraments';

const LIST = '/admin/events';

interface EventFormValues {
  description: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

const EMPTY: EventFormValues = {
  description: '',
  startDate: '',
  endDate: '',
  isActive: true,
};

export function EventForm({ id }: { id?: string }) {
  const navigate = useNavigate();
  const { data: event, isLoading } = useEvent(id ?? '');
  const create = useCreateEvent();
  const update = useUpdateEvent();
  const remove = useDeleteEvent();
  const saving = create.isPending || update.isPending;

  const form = useForm<EventFormValues>({
    resolver: zodResolver(createEventSchema) as unknown as Resolver<EventFormValues>,
    defaultValues: EMPTY,
  });
  const { errors } = form.formState;

  useServerErrors(form, create.error ?? update.error);

  useEffect(() => {
    if (!event) return;
    form.reset({
      description: event.description,
      startDate: date(event.startDate).format('YYYY-MM-DD'),
      endDate: date(event.endDate).format('YYYY-MM-DD'),
      isActive: event.isActive,
    });
  }, [event]);

  // Envia as datas como YYYY-MM-DD; o servidor ajusta início/fim do dia no fuso local.
  const handleSubmit = form.handleSubmit(async () => {
    const values = form.getValues();
    const data = values as unknown as CreateEventSchema;
    try {
      if (id) await update.mutateAsync({ id, data });
      else await create.mutateAsync(data);
      navigate(LIST);
    } catch {
      // erros exibidos via toast / useServerErrors
    }
  });

  const handleDelete = async () => {
    if (!id) return;
    try {
      await remove.mutateAsync(id);
      navigate(LIST);
    } catch {
      // "Este encontro possui casais vinculados." chega pelo toast
    }
  };

  return (
    <>
      <ContentLoader show={(!!id && isLoading) || saving || remove.isPending} />
      <AdminFormPage
        backTo={LIST}
        backLabel="Voltar para Encontros"
        eyebrow="ENCONTROS"
        title={id ? 'Editar encontro' : 'Novo encontro'}
        description="Defina a descrição e as datas da edição do retiro."
        onSubmit={handleSubmit}
        onCancel={() => navigate(LIST)}
        saveLabel={id ? 'Salvar alterações' : 'Criar cadastro'}
        saving={saving}
        onDelete={id ? handleDelete : undefined}
      >
        <FormSection
          eyebrow="DADOS DO ENCONTRO"
          title="Informações gerais"
          className="rounded-[18px]"
        >
          <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
            <TextField
              wrapperClassName="col-span-full"
              label="Descrição *"
              placeholder="Ex: Retiro Enchei-vos 2027"
              error={errors.description?.message}
              {...form.register('description')}
            />
            <TextField
              label="Data de início *"
              type="date"
              error={errors.startDate?.message}
              {...form.register('startDate')}
            />
            <TextField
              label="Data de término *"
              type="date"
              error={errors.endDate?.message}
              {...form.register('endDate')}
            />
            <Controller
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <ChoiceGroup
                  className="col-span-full"
                  label="Status"
                  options={ACTIVE_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </FormSection>
      </AdminFormPage>
    </>
  );
}
